import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Search, Download, Clock, Filter } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function AuditTrailPage({ onLogout, userRole, isFeatureEnabled }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [availableActions, setAvailableActions] = useState([]);
  const [tab, setTab] = useState("audit"); // audit | access

  const [accessEntries, setAccessEntries] = useState([]);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (search) params.set("search", search);
      if (actionFilter) params.set("action", actionFilter);
      const resp = await axios.get(`${API}/admin/audit-log?${params}`, { headers: getAuthHeaders() });
      setEntries(resp.data.entries || []);
      setAvailableActions(resp.data.available_actions || []);
    } catch { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  }, [search, actionFilter]);

  const fetchAccess = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/document-access-log?limit=500`, { headers: getAuthHeaders() });
      setAccessEntries(resp.data.entries || []);
    } catch {}
  }, []);

  useEffect(() => { fetchAudit(); fetchAccess(); }, [fetchAudit, fetchAccess]);

  const handleExport = async () => {
    try {
      const resp = await axios.get(`${API}/admin/audit-log/export`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch { toast.error("Export failed"); }
  };

  const actionColor = (action) => {
    if (action.includes("delete")) return "#DC2626";
    if (action.includes("create") || action.includes("upload")) return "#10B981";
    if (action.includes("update") || action.includes("config")) return "#D97706";
    if (action.includes("login") || action.includes("badge")) return "#2563EB";
    return "#64748B";
  };

  const data = tab === "audit" ? entries : accessEntries;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="audit-trail-page">
        <PageHeader title="Audit Trail" subtitle="Complete activity log for compliance and security review" testId="audit-title">
          <Button onClick={handleExport} variant="outline" className="rounded-none border-[#E2E8F0]" data-testid="export-audit-btn">
            <Download size={14} className="mr-2" />Export CSV
          </Button>
        </PageHeader>

        {/* Tabs */}
        <div className="flex border-b-2 border-[#E2E8F0]">
          {[{ key: "audit", label: "System Audit Log", count: entries.length }, { key: "access", label: "Document Access Log", count: accessEntries.length }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-6 py-3 text-sm font-bold tracking-wide relative ${tab === t.key ? "text-[#DC2626]" : "text-[#78716C] hover:text-[#1C1917]"}`} data-testid={`tab-${t.key}`}>
              {t.label} <span className="ml-1 text-xs font-mono">({t.count})</span>
              {tab === t.key && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#DC2626]" />}
            </button>
          ))}
        </div>

        {/* Filters */}
        {tab === "audit" && (
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search actions, users..." className="pl-10 rounded-none" data-testid="audit-search" />
            </div>
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="h-10 px-3 border border-[#E2E8F0] bg-white text-sm min-w-[200px]" data-testid="audit-action-filter">
              <option value="">All Actions</option>
              {availableActions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            {tab === "audit" ? (
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0] sticky top-0">
                  <tr>
                    {["Timestamp", "Action", "User", "Details"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-[#0F172A] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.map(e => (
                    <tr key={e.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-4 py-3 font-mono text-xs text-[#64748B] whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: actionColor(e.action), backgroundColor: `${actionColor(e.action)}10` }}>{e.action}</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-[#0F172A]">{e.user_id}</td>
                      <td className="px-4 py-3 text-xs text-[#64748B] max-w-[400px] truncate">{JSON.stringify(e.details)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm" data-testid="access-table">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0] sticky top-0">
                  <tr>
                    {["Timestamp", "Action", "User", "Role", "Document", "Blueprint ID"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-[#0F172A] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {accessEntries.map(e => (
                    <tr key={e.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-4 py-3 font-mono text-xs text-[#64748B] whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${e.access_type === "download" ? "bg-[#FEF2F2] text-[#DC2626]" : "bg-[#EFF6FF] text-[#2563EB]"}`}>{e.access_type}</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-[#0F172A]">{e.user_name || e.user_id}</td>
                      <td className="px-4 py-3 text-xs text-[#64748B] uppercase">{e.user_role}</td>
                      <td className="px-4 py-3 text-xs font-mono text-[#0F172A] truncate max-w-[250px]">{e.filename}</td>
                      <td className="px-4 py-3 text-xs font-mono text-[#94A3B8]">{e.blueprint_id?.slice(0, 8)}...</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data.length === 0 && !loading && (
            <div className="p-8 text-center text-[#94A3B8]">
              <Shield size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No entries found</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
